import type { ProductGetResult } from "@/dao/product";
import { formatMoney } from "@/lib/currencies";
import { cn } from "@/lib/utils";
import { AddToCartButton } from "./AddToCartButton";
import { MainProductImage } from "./products/main-product-image";

export const ProductBottomStickyCard = ({
	product,
	variant,
	currency,
	locale,
	domain,
	theme,
	show,
}: {
	product: ProductGetResult;
	variant: ProductGetResult["variants"][number] | undefined;
	currency: string;
	locale: string;
	show: boolean;
	domain: string;
	theme: {
		color?: string | null;
		buttonBackgroundColor?: string | null;
		buttonHoverBackgroundColor?: string | null;
		buttonTextColor?: string | null;
		priceColor?: string | null;
	};
}) => {
	if (!variant) {
		return null;
	}

	return (
		<div
			tabIndex={show ? 0 : -1}
			className={cn(
				"fixed bottom-0 max-w-[100vw] left-0 right-0 bg-white/90 backdrop-blur-xs border-t py-2 sm:py-4 transition-all duration-300 ease-out z-10",
				show
					? "transform translate-y-0 shadow-[0_-4px_6px_-1px_rgb(0_0_0_/_0.1),_0_-2px_4px_-2px_rgb(0_0_0_/_0.1)]"
					: "transform translate-y-full",
			)}
		>
			<div className="mx-auto w-full max-w-7xl gap-x-2 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
				<div className="flex items-center gap-x-2 sm:gap-x-4 min-w-0">
					<div className="shrink-0">
						{variant.images[0] && (
							<MainProductImage
								className="w-16 h-16 rounded-lg bg-neutral-100 object-cover object-center"
								src={variant.images[0]}
								loading="eager"
								priority
								alt={product.name}
							/>
						)}
					</div>
					<div className="flex-1 min-w-0">
						<h3 className="font-semibold text-xs sm:text-base md:text-lg whitespace-nowrap text-ellipsis overflow-clip">
							{product.name}
						</h3>

						<p className="text-xs sm:text-sm">
							{formatMoney({
								amount: variant.price,
								currency: currency,
								locale,
							})}
						</p>
					</div>
				</div>

				<AddToCartButton
					variant={variant}
					product={product}
					domain={domain}
					style={{
						"--theme-product-details-add-to-cart-button-color": theme.buttonTextColor ?? undefined,
						"--theme-product-details-add-to-cart-button-background-color":
							theme.buttonBackgroundColor ?? undefined,
						"--theme-product-details-add-to-cart-button-hover-background-color":
							theme.buttonHoverBackgroundColor ?? undefined,
						"--theme-product-details-price-color": theme.priceColor ?? undefined,
					}}
					className={cn(
						"text-[var(--theme-product-details-add-to-cart-button-color,#fff)]",
						"bg-[var(--theme-product-details-add-to-cart-button-background-color,#000)]",
						"hover:bg-[var(--theme-product-details-add-to-cart-button-hover-background-color,#222)]",
					)}
				/>
			</div>
		</div>
	);
};
